import { useEffect, useCallback, useRef } from 'react';

/**
 * Hook that detects clicks outside of specified elements and focus changes for iframes.
 * 
 * @param {Function | Function[]} actions - Single callback function or array of callback functions to execute
 * @param {React.RefObject | React.RefObject[]} refs - Single ref or array of refs to monitor
 * @param {boolean} isActive - Whether the hook should be active
 * @returns {void}
 */

const useClickOutside = (actions, refs, isActive = true) => {
  // Store the normalized actions and refs in refs to avoid dependency issues
  const actionsRef = useRef(Array.isArray(actions) ? actions : [actions]);
  const refsRef = useRef(Array.isArray(refs) ? refs : [refs]);
  const isActiveRef = useRef(isActive);
  
  // Update refs when props change
  useEffect(() => {
    actionsRef.current = Array.isArray(actions) ? actions : [actions];
    refsRef.current = Array.isArray(refs) ? refs : [refs];
    isActiveRef.current = isActive;
  }, [actions, refs, isActive]);

  const handleClickOutside = useCallback((event) => {
    if (!isActiveRef.current) return;
    
    const currentRefs = refsRef.current;
    const currentActions = actionsRef.current;
    
    currentRefs.forEach((ref, index) => {
      const action = currentActions[index] || currentActions[0];
      
      // Skip if ref is invalid or contains the target
      if (!ref?.current || ref.current.contains(event.target)) {
        return;
      }
      
      // Execute corresponding action or fallback to first action
      if (typeof action === 'function') {
        action(event);
      }
    });
  }, []);

  const handleIframeFocus = useCallback((event) => {
    if (!isActiveRef.current) return;
    
    // Use requestAnimationFrame for better browser compatibility and performance
    requestAnimationFrame(() => {
      if (document.activeElement.tagName === "IFRAME") {
        const currentActions = actionsRef.current;
        
        refsRef.current.forEach((_, index) => {
          const action = currentActions[index] || currentActions[0];
          if (typeof action === 'function') {
            action(event);
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!isActive) return;
    
    // Use capture phase for mousedown to ensure this runs before other handlers
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener("blur",handleIframeFocus , { once: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleIframeFocus, { once: true });
    };
  }, [isActive]);
};

export default useClickOutside;